"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useSearchParams } from "next/navigation";

import { Section, SectionHeader } from "@/components/layout";
import type { BaseWithRelations } from "@/types/base";
import type { CategoryRecord } from "@/types/category";

import BaseGridClient from "../BaseGrid/BaseGridClient";
import BaseGridSkeleton from "../BaseGrid/BaseGridSkeleton";
import BaseRelatedDisplayAd from "../BaseGrid/RelatedBasesIsland/BaseRelatedDisplayAd";

type DefaultMode = "recent" | "recommended";
type InitialKind = "recent" | "recommended" | "category";

interface Props {
  levelSlug: string;
  initialBases: BaseWithRelations[];
  categories: CategoryRecord[];
  defaultTitle?: string;
  defaultMode?: DefaultMode;
  initialKind?: InitialKind;
  initialOffset?: number;
  adSlot?: string;
  adContextKeyPrefix?: string;
  injectAdEvery?: number;
}

const INITIAL_LIMIT = 6;
const LOAD_MORE_LIMIT = 6;

function getSplitIndex(viewportWidth: number) {
  if (viewportWidth >= 1024) return 4;
  if (viewportWidth >= 768) return 3;
  return 2;
}

export default function FilterableBaseGrid({
  levelSlug,
  initialBases,
  categories,
  defaultTitle = "Recent Bases",
  defaultMode = "recent",
  initialKind = defaultMode === "recent" ? "recent" : "recommended",
  initialOffset = 0,
  adSlot,
  adContextKeyPrefix,
}: Props) {
  const params = useSearchParams();
  const category = params.get("category");
  const view = params.get("view");

  const seededInitial = initialBases.slice(0, INITIAL_LIMIT);

  const [bases, setBases] = useState<BaseWithRelations[]>(seededInitial);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(initialOffset + seededInitial.length);
  const [hasMore, setHasMore] = useState(
    seededInitial.length > 0 ? initialBases.length > seededInitial.length : true
  );
  const [viewportWidth, setViewportWidth] = useState<number>(1440);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const activeCategory = useMemo(() => {
    if (!category) return null;
    return categories.find((c) => c.slug === category) ?? null;
  }, [category, categories]);

  const title = activeCategory ? `${activeCategory.title} Bases` : defaultTitle;
  const showRecentTime = !category;

  const queryKey = `${levelSlug}|${category ?? ""}|${view ?? ""}|${defaultMode}|${initialKind}|${initialOffset}`;

  const fetchJson = useCallback(async (url: string, signal?: AbortSignal) => {
    const res = await fetch(url, {
      signal,
      headers: { Accept: "application/json" },
    });

    if (!res.ok) {
      throw new Error(`Fetch failed (${res.status})`);
    }

    return (await res.json()) as BaseWithRelations[];
  }, []);

  const buildUrl = useCallback(
    (nextOffset: number, limit: number) => {
      const qs = new URLSearchParams();
      qs.set("level", levelSlug);
      qs.set("limit", String(limit));
      qs.set("offset", String(nextOffset));

      if (category) {
        qs.set("category", category);
      } else {
        qs.set("view", "recent");
      }

      return `/api/bases?${qs.toString()}`;
    },
    [levelSlug, category]
  );

  useEffect(() => {
    const updateViewportWidth = () => {
      setViewportWidth(window.innerWidth || 1440);
    };

    updateViewportWidth();
    window.addEventListener("resize", updateViewportWidth, { passive: true });

    return () => {
      window.removeEventListener("resize", updateViewportWidth);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    async function loadInitial() {
      const shouldUseSeededRecent =
        !category &&
        (view === "recent" || !view) &&
        defaultMode === "recent" &&
        initialKind === "recent" &&
        initialBases.length > 0;

      const shouldUseSeededRecommended =
        !category &&
        !view &&
        defaultMode === "recommended" &&
        initialKind === "recommended" &&
        initialBases.length > 0;

      if (shouldUseSeededRecent || shouldUseSeededRecommended) {
        const seeded = initialBases.slice(0, INITIAL_LIMIT);

        setBases(seeded);
        setOffset(initialOffset + seeded.length);
        setHasMore(initialBases.length > seeded.length);
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      setLoading(true);
      setLoadingMore(false);

      try {
        const data = await fetchJson(
          buildUrl(initialOffset, INITIAL_LIMIT),
          controller.signal
        );

        setBases(data);
        setOffset(initialOffset + data.length);
        setHasMore(data.length === INITIAL_LIMIT);
      } catch (e) {
        if (!(e instanceof DOMException && e.name === "AbortError")) {
          console.error(e);

          const fallback = initialBases.slice(0, INITIAL_LIMIT);
          setBases(fallback);
          setOffset(initialOffset + fallback.length);
          setHasMore(
            fallback.length > 0 && initialBases.length > fallback.length
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadInitial();

    return () => controller.abort();
  }, [
    queryKey,
    initialBases,
    category,
    view,
    defaultMode,
    initialKind,
    initialOffset,
    fetchJson,
    buildUrl,
  ]);

  useEffect(() => {
    if (!sentinelRef.current || loading || loadingMore || !hasMore) return;

    const el = sentinelRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;

        observer.unobserve(el);
        setLoadingMore(true);

        void (async () => {
          try {
            const data = await fetchJson(buildUrl(offset, LOAD_MORE_LIMIT));

            if (data.length === 0) {
              setHasMore(false);
              return;
            }

            setBases((prev) => {
              const seen = new Set(prev.map((b) => b.id));
              const merged = [...prev];

              for (const item of data) {
                if (!seen.has(item.id)) {
                  merged.push(item);
                }
              }

              return merged;
            });

            setOffset((prev) => prev + data.length);
            setHasMore(data.length === LOAD_MORE_LIMIT);
          } catch (e) {
            console.error(e);
          } finally {
            setLoadingMore(false);
          }
        })();
      },
      {
        rootMargin: "150px 0px",
      }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [offset, hasMore, loading, loadingMore, fetchJson, buildUrl]);

  const splitIndex = useMemo(
    () => getSplitIndex(viewportWidth),
    [viewportWidth]
  );

  const canSplitForAd = bases.length > splitIndex;
  const primaryBases = canSplitForAd ? bases.slice(0, splitIndex) : bases;
  const remainingBases = canSplitForAd ? bases.slice(splitIndex) : [];

  const shouldRenderAd =
    Boolean(adSlot) &&
    Boolean(adContextKeyPrefix) &&
    remainingBases.length > 0;

  return (
    <Section>
      <section className="stack-lg">
        <div className="px-lg">
          <div className="py-sm stack-Lg">
            <SectionHeader title={title} />

            {loading ? (
              <BaseGridSkeleton
                count={INITIAL_LIMIT}
                variant="default"
                mobileBleed={false}
              />
            ) : (
              <BaseGridClient
                bases={primaryBases}
                showTime={showRecentTime}
                showMeta="category"
                mobileBleed={false}
              />
            )}
          </div>
        </div>

        {!loading && shouldRenderAd ? (
          <BaseRelatedDisplayAd slot={adSlot!} />
        ) : null}

        {!loading && remainingBases.length > 0 ? (
          <div className="px-lg">
            <BaseGridClient
              bases={remainingBases}
              showTime={showRecentTime}
              showMeta="category"
              mobileBleed={false}
            />
          </div>
        ) : null}

        {!loading ? (
          <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
        ) : null}

        {loadingMore ? (
          <div className="px-lg">
            <BaseGridSkeleton
              count={LOAD_MORE_LIMIT}
              variant="default"
              mobileBleed={false}
            />
          </div>
        ) : null}
      </section>
    </Section>
  );
}